import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import {
  FaFireAlt,
  FaSearch,
  FaTrash,
  FaEye,
  FaFileExcel,
  FaUsers,
  FaCalendarCheck,
  FaStar,
  FaPhoneAlt,
  FaWhatsapp,
  FaCheck,
  FaTimes,
  FaCopy,
  FaSyncAlt,
  FaPaperPlane,
  FaEnvelope,
  FaSave,
} from "react-icons/fa";
import { useHomam } from "../../context/HomamContext";
import { MOCK_NAKSHATRAS } from "../../data/homamData";
import "../styles/ManageHomamBookings.css";

const ManageHomamBookings = () => {
  const API = API_ENDPOINTS.HOMAM_BOOKINGS || `${API_ENDPOINTS.API_BASE}/homam/bookings`;
  const EXPORT_API = API_ENDPOINTS.HOMAM_EXPORT || `${API_ENDPOINTS.API_BASE}/homam/export`;
  const SETTINGS_API = API_ENDPOINTS.HOMAM_SETTINGS || `${API_ENDPOINTS.API_BASE}/homam/settings` || "http://localhost:5000/api/homam/settings";

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [starFilter, setStarFilter] = useState("All");
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Custom UI Modals & Notifications
  const [approvalModalData, setApprovalModalData] = useState(null);
  const [rejectModalData, setRejectModalData] = useState(null);
  const [toast, setToast] = useState(null);
  const [resendingWa, setResendingWa] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // Homam Visibility & WhatsApp Dynamic Settings
  const {
    isHomamActive,
    updateHomamActive,
    whatsappNumber,
    formattedWhatsAppNumber,
    updateWhatsAppNumber,
  } = useHomam();

  const getAdminAuthHeader = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return {};
    return { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` };
  };

  // WhatsApp Device Pairing & Connection States
  const [waStatus, setWaStatus] = useState({
    connected: false,
    isRegistered: false,
    connectedNumber: (whatsappNumber && !whatsappNumber.includes("9080845897")) ? whatsappNumber : "916369482332",
    formattedNumber: (formattedWhatsAppNumber && !formattedWhatsAppNumber.includes("90808")) ? formattedWhatsAppNumber : "+91 63694 82332",
    pairingCode: null,
  });
  // Track consecutive disconnected polls to avoid flicker
  const disconnectedCountRef = React.useRef(0);
  const [showWaPairModal, setShowWaPairModal] = useState(false);
  const [waModalMode, setWaModalMode] = useState("auto"); // "connected" | "pair"
  const [requestingPairCode, setRequestingPairCode] = useState(false);
  const [savingNumberOnly, setSavingNumberOnly] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Dynamic admin-entered phone number (defaults to connected or configured number)
  const [pairingPhoneInput, setPairingPhoneInput] = useState(() => {
    const num = (whatsappNumber && !whatsappNumber.includes("9080845897")) ? whatsappNumber : "916369482332";
    return num.replace(/\D/g, "").slice(-10);
  });

  // Number for which pairing code was generated
  const [codeGeneratedForNumber, setCodeGeneratedForNumber] = useState(() => {
    const num = (whatsappNumber && !whatsappNumber.includes("9080845897")) ? whatsappNumber : "916369482332";
    return num.replace(/\D/g, "").slice(-10);
  });

  // Dynamic test message recipient (strictly synced to admin entered / paired number)
  const [testMobile, setTestMobile] = useState(() => {
    const num = (whatsappNumber && !whatsappNumber.includes("9080845897")) ? whatsappNumber : "916369482332";
    return num.replace(/\D/g, "").slice(-10);
  });
  const [sendingTestWa, setSendingTestWa] = useState(false);

  // Synchronize inputs ONLY on initial load when empty
  useEffect(() => {
    if (whatsappNumber && !pairingPhoneInput) {
      const clean10 = whatsappNumber.replace(/\D/g, "").slice(-10);
      setPairingPhoneInput(clean10);
      setTestMobile(clean10);
      setCodeGeneratedForNumber(clean10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappNumber]);

  const fetchWhatsAppStatus = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/whatsapp/status`);
      const data = await res.json();
      if (data?.success && data?.data) {
        const incoming = data.data;

        // Debounce: only accept a "disconnected" state after 2 consecutive disconnected polls
        // This prevents a single transient blip from flipping the UI badge to red
        if (incoming.connected) {
          disconnectedCountRef.current = 0;
          setWaStatus(incoming);
        } else {
          disconnectedCountRef.current += 1;
          if (disconnectedCountRef.current >= 2) {
            // Confirmed disconnected — update UI
            setWaStatus(incoming);
          }
          // If only 1 poll says disconnected, keep showing last known state (no flicker)
        }

        const activeNum = incoming.connectedNumber || incoming.configuredNumber;
        if (activeNum) {
          const clean = String(activeNum).replace(/\D/g, "");
          const clean10 = clean.slice(-10);
          const fmt = incoming.formattedNumber || (clean10.length === 10 ? `+91 ${clean10.slice(0, 5)} ${clean10.slice(5)}` : `+${clean}`);
          updateWhatsAppNumber(clean, fmt);
        }
      }
    } catch (_) {}
  };

  useEffect(() => {
    fetchWhatsAppStatus();
    const timer = setInterval(fetchWhatsAppStatus, showWaPairModal ? 3000 : 8000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWaPairModal]);

  const handlePhoneInputChange = (val) => {
    const clean10 = val.replace(/\D/g, "").slice(-10);
    setPairingPhoneInput(clean10);
    setTestMobile(clean10);
    setCodeGeneratedForNumber(clean10);
  };

  const handleClearPhoneInput = () => {
    setPairingPhoneInput("");
    setTestMobile("");
  };

  const handleRequestPairingCode = async (customPhone) => {
    const rawTarget = customPhone || pairingPhoneInput || whatsappNumber || "9080845897";
    const clean10 = String(rawTarget).replace(/\D/g, "").slice(-10);
    if (!clean10 || clean10.length < 10) {
      showToast("Please enter a valid 10-digit mobile number", "error");
      return;
    }
    try {
      setRequestingPairCode(true);
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/whatsapp/pair`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: clean10 }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const fullNum = `91${clean10}`;
        const fmtNum = `+91 ${clean10.slice(0, 5)} ${clean10.slice(5)}`;
        setCodeGeneratedForNumber(clean10);
        setTestMobile(clean10); // Sync test message field
        setPairingPhoneInput(clean10);
        setWaStatus((prev) => ({
          ...prev,
          pairingCode: data.pairingCode,
          connected: false,
          connectedNumber: fullNum,
          formattedNumber: fmtNum,
        }));
        updateWhatsAppNumber(fullNum, fmtNum);
        showToast(`✨ Fresh Pairing Code generated for ${fmtNum}: ${data.pairingCode}`, "success");
      } else {
        showToast(data.message || "Failed to generate pairing code", "error");
      }
    } catch (err) {
      showToast("Error requesting WhatsApp pairing code", "error");
    } finally {
      setRequestingPairCode(false);
    }
  };

  const handleSaveWhatsAppNumberOnly = async (customPhone) => {
    const rawTarget = customPhone || pairingPhoneInput || whatsappNumber || "9080845897";
    const clean10 = String(rawTarget).replace(/\D/g, "").slice(-10);
    if (!clean10 || clean10.length < 10) {
      showToast("Please enter a valid 10-digit mobile number", "error");
      return;
    }
    try {
      setSavingNumberOnly(true);
      const fullNum = `91${clean10}`;
      const fmtNum = `+91 ${clean10.slice(0, 5)} ${clean10.slice(5)}`;
      setTestMobile(clean10); // Sync test message field
      setCodeGeneratedForNumber(clean10);
      setPairingPhoneInput(clean10);
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/whatsapp/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminAuthHeader(),
        },
        body: JSON.stringify({ whatsappNumber: fullNum }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        updateWhatsAppNumber(fullNum, fmtNum);
        setWaStatus((prev) => ({
          ...prev,
          connectedNumber: fullNum,
          formattedNumber: fmtNum,
        }));
        showToast(`✅ WhatsApp number saved: ${fmtNum}. Updated across website!`, "success");
      } else {
        showToast(data.message || "Failed to save WhatsApp number", "error");
      }
    } catch (err) {
      showToast("Error saving WhatsApp number", "error");
    } finally {
      setSavingNumberOnly(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMobile) return;
    try {
      setSendingTestWa(true);
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/whatsapp/send-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber: testMobile }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`✅ Test message sent to +91 ${testMobile.slice(-10)}!`, "success");
      } else {
        showToast(data.message || "Failed to send test message", "error");
      }
    } catch (err) {
      showToast("Error contacting WhatsApp service", "error");
    } finally {
      setSendingTestWa(false);
    }
  };

  // Prevent background page scrolling while WhatsApp popup is active
  useEffect(() => {
    if (showWaPairModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showWaPairModal]);

  const handleDisconnectWhatsApp = async () => {
    const activeNum = waStatus.formattedNumber || formattedWhatsAppNumber || "+91 90808 45897";
    if (!window.confirm(`Are you sure you want to disconnect WhatsApp (${activeNum})? Devotee automated alerts will be paused until reconnected.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/whatsapp/disconnect`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.success) {
        showToast("WhatsApp unlinked successfully. You can pair anytime.", "info");
        setWaModalMode("pair");
        setPairingPhoneInput("");
        setTestMobile("");
        fetchWhatsAppStatus();
      } else {
        showToast(data.message || "Failed to disconnect WhatsApp", "error");
      }
    } catch (_) {
      showToast("Error contacting WhatsApp service", "error");
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const handleSendWhatsAppNotification = async (booking) => {
    if (!booking) return;
    try {
      setResendingWa(true);
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/homam/bookings/${booking._id || booking.bookingId}/send-whatsapp`, {
        method: "POST",
        headers: {
          ...getAdminAuthHeader(),
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`✅ WhatsApp message delivered to +91 ${(booking.whatsappNumber || booking.mobile).slice(-10)}`, "success");
      } else {
        showToast(data.message || "Failed to send WhatsApp message", "error");
      }
    } catch (err) {
      console.error("WhatsApp send error:", err);
      showToast("Error connecting to server to send WhatsApp message", "error");
    } finally {
      setResendingWa(false);
    }
  };

  const handleSendEmailNotification = async (booking) => {
    if (!booking) return;
    try {
      setResendingEmail(true);
      const res = await fetch(`${API_ENDPOINTS.API_BASE || "http://localhost:5000/api"}/homam/bookings/${booking._id || booking.bookingId}/send-email`, {
        method: "POST",
        headers: {
          ...getAdminAuthHeader(),
        },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`📧 Official Mandir notification email sent successfully to devotee!`, "success");
      } else {
        showToast(data.message || "Failed to send notification email", "error");
      }
    } catch (err) {
      console.error("Email send error:", err);
      showToast("Error connecting to server to send notification email", "error");
    } finally {
      setResendingEmail(false);
    }
  };

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3800);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Global Visibility Toggle
  const [toggleLoading, setToggleLoading] = useState(false);

  const handleToggleStatus = async () => {
    const newStatus = !isHomamActive;
    updateHomamActive(newStatus);
    setToggleLoading(true);

    try {
      const res = await fetch(SETTINGS_API, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminAuthHeader(),
        },
        body: JSON.stringify({ isHomamActive: newStatus }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        console.log(`✅ Homam visibility set to: ${newStatus ? "Active" : "Hidden"}`);
        showToast(`Homam visibility set to: ${newStatus ? "Active on Website" : "Hidden from Website"}`, "info");
      } else if (res.status === 401) {
        updateHomamActive(!newStatus);
        showToast("Session expired. Please log out and log in again to the admin panel.", "error");
      } else {
        updateHomamActive(!newStatus);
        showToast(data.message || "Failed to update website visibility. Please try again.", "error");
      }
    } catch (err) {
      updateHomamActive(!newStatus);
      console.error("Toggle error:", err);
      showToast("Network error — could not update website visibility.", "error");
    } finally {
      setToggleLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        console.warn("Admin session not found. Please log in at /admin/login");
        setBookings([]);
        return;
      }
      const authHeader = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      const res = await fetch(API, {
        headers: { Authorization: authHeader },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setBookings(Array.isArray(data.bookings) ? data.bookings : []);
      } else {
        console.error("Fetch homam bookings error:", data.message);
        setBookings([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this devotee registration record?"
    );
    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API}/${id}`, {
        method: "DELETE",
        headers: { ...getAdminAuthHeader() },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setBookings((prev) => prev.filter((item) => item._id !== id));
        if (selectedBooking && selectedBooking._id === id) {
          setSelectedBooking(null);
        }
        showToast("🎉 Devotee registration deleted successfully", "success");
      } else {
        showToast(data.message || "Failed to delete booking", "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast("Error deleting registration record.", "error");
    }
  };

  const handleVerifyPayment = async (id, action, customReason = "") => {
    if (action === "reject" && !customReason) {
      const target = bookings.find((b) => b._id === id) || selectedBooking;
      setRejectModalData({ id, booking: target, reason: "" });
      return;
    }

    try {
      const url = API_ENDPOINTS.HOMAM_VERIFY_PAYMENT
        ? API_ENDPOINTS.HOMAM_VERIFY_PAYMENT(id)
        : `${API_ENDPOINTS.API_BASE}/homam/bookings/${id}/verify-payment`;

      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAdminAuthHeader(),
        },
        body: JSON.stringify({ action, rejectionReason: customReason }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBookings((prev) =>
          prev.map((b) => (b._id === id ? { ...b, ...data.booking } : b))
        );
        if (selectedBooking && selectedBooking._id === id) {
          setSelectedBooking((prev) => ({ ...prev, ...data.booking }));
        }
        if (action === "approve") {
          setApprovalModalData(data.booking);
          const waSuccess = data.whatsappDelivery?.success;
          const emailSuccess = data.emailDelivery?.success;
          if (waSuccess && emailSuccess) {
            showToast(`🎉 Registration confirmed! WhatsApp & confirmation email sent to devotee.`, "success");
          } else if (waSuccess) {
            showToast(`🎉 Registration confirmed & WhatsApp notification sent! (Email: ${data.emailDelivery?.reason || "Delivery pending"})`, "success");
          } else if (emailSuccess) {
            showToast(`🎉 Registration confirmed & confirmation email sent to ${data.emailDelivery?.recipient || "devotee"}.`, "success");
          } else {
            showToast(`🎉 Registration confirmed! (Notifications queued)`, "info");
          }
        } else {
          setRejectModalData(null);
          const waSuccess = data.whatsappDelivery?.success;
          const emailSuccess = data.emailDelivery?.success;
          const devoteeName = data.booking?.name || "devotee";

          if (waSuccess && emailSuccess) {
            showToast(`❌ Registration rejected & notification delivered via WhatsApp & Email to ${devoteeName}.`, "info");
          } else if (waSuccess) {
            showToast(`❌ Registration rejected & WhatsApp notice sent to ${devoteeName}. (Email: ${data.emailDelivery?.reason || data.emailDelivery?.error || "skipped"})`, "info");
          } else if (emailSuccess) {
            showToast(`❌ Registration rejected & official notice email sent to ${data.emailDelivery?.recipient || devoteeName}.`, "info");
          } else {
            showToast(`❌ Registration marked as rejected for ${devoteeName}.`, "info");
          }
        }
      } else {
        showToast(data.message || "Failed to update payment verification", "error");
      }
    } catch (err) {
      console.error("Payment verification error:", err);
      showToast("Error connecting to server for payment verification", "error");
    }
  };


  // 1-Click Excel Download Handler (Exports filtered star or all records as a styled .xlsx spreadsheet)
  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      showToast("Preparing Excel spreadsheet (.xlsx)...", "info");

      const queryParams = new URLSearchParams();
      if (starFilter && starFilter !== "All") {
        queryParams.append("nakshatraId", starFilter);
      }
      if (search && search.trim()) {
        queryParams.append("search", search.trim());
      }

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const res = await fetch(`${EXPORT_API}${queryString}`, {
        headers: { ...getAdminAuthHeader() },
      });

      const dateStr = new Date().toISOString().split("T")[0];
      let fileSlug = "All_27_Nakshatras";
      if (starFilter && starFilter !== "All") {
        const matched = MOCK_NAKSHATRAS.find((s) => String(s.id) === String(starFilter));
        fileSlug = `Day_${starFilter}_${matched?.nameEn?.replace(/[^a-zA-Z0-9]/g, "_") || `Star_${starFilter}`}`;
      }

      if (res.ok) {
        const blob = await res.blob();
        // Create an official OpenXML Excel spreadsheet blob (.xlsx)
        const excelBlob = new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(excelBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Nakshatra_Homam_${fileSlug}_${dateStr}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showToast("🎉 Excel spreadsheet downloaded successfully!", "success");
      } else {
        const errData = await res.json().catch(() => ({}));
        showToast(errData.message || "Failed to download Excel file from server.", "error");
      }
    } catch (err) {
      console.error("Excel export error:", err);
      showToast("Error downloading Excel spreadsheet from server.", "error");
    } finally {
      setDownloadingExcel(false);
    }
  };

  // Filter Bookings
  const filteredBookings = bookings.filter((item) => {
    const name = (item.name || "").toLowerCase();
    const mobile = (item.mobile || "").toLowerCase();
    const wa = (item.whatsappNumber || "").toLowerCase();
    const city = (item.city || "").toLowerCase();
    const starName = (item.nakshatraNameEn || "").toLowerCase();
    const starNameTa = (item.nakshatraNameTa || "").toLowerCase();
    const preferredDay = (item.preferredDay || "").toLowerCase();
    const bookingId = (item.bookingId || "").toLowerCase();
    const utr = (item.utrNumber || "").toLowerCase();
    const q = search.trim().toLowerCase();

    const matchesSearch =
      !q ||
      name.includes(q) ||
      mobile.includes(q) ||
      wa.includes(q) ||
      city.includes(q) ||
      starName.includes(q) ||
      starNameTa.includes(q) ||
      preferredDay.includes(q) ||
      bookingId.includes(q) ||
      utr.includes(q);

    const selectedStarObj = MOCK_NAKSHATRAS.find((s) => String(s.id) === String(starFilter));

    const matchesStar = (() => {
      if (starFilter === "All" || !selectedStarObj) return true;

      // 1. If booking has English Star Name, strictly check name
      if (item.nakshatraNameEn) {
        return item.nakshatraNameEn.trim().toLowerCase() === selectedStarObj.nameEn.toLowerCase();
      }
      // 2. If booking has Tamil Star Name
      if (item.nakshatraNameTa) {
        return item.nakshatraNameTa.trim() === selectedStarObj.nameTa;
      }
      // 3. If booking has preferredDay string (e.g. "Day 1: Ashwini")
      if (item.preferredDay) {
        return item.preferredDay.toLowerCase().includes(selectedStarObj.nameEn.toLowerCase());
      }
      // 4. Fallback if no names stored
      return String(item.nakshatraId) === String(selectedStarObj.id) ||
             String(item.nakshatraId) === String(selectedStarObj.orderIndex);
    })();

    return matchesSearch && matchesStar;
  });

  const totalParticipants = bookings.reduce(
    (sum, b) => sum + Number(b.participantsCount || 1),
    0
  );

  const todayBookingsCount = bookings.filter((b) => {
    const today = new Date().toDateString();
    return new Date(b.createdAt).toDateString() === today;
  }).length;

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="manage-homam-container">
      {/* BANNER */}
      <div className="homam-banner">
        <div className="homam-banner-content">
          <h1>
            <FaFireAlt /> Nakshatra Homam Registrations
          </h1>
          <p>
            Review all devotees registered for the 27 Nakshatra Maha Homam, inspect their birth star details, and download the official devotees list.
          </p>
        </div>

        <div className="homam-banner-controls">
          {/* WHATSAPP BOT STATUS & PAIRING CARD */}
          <div
            className={`wa-header-status-card ${waStatus.connected ? "connected" : "disconnected"}`}
            onClick={() => {
              setShowWaPairModal(true);
              setWaModalMode(waStatus.connected ? "connected" : "pair");
            }}
            style={{ cursor: "pointer" }}
            title={
              waStatus.connected
                ? `WhatsApp Bot is Connected (${waStatus.formattedNumber || formattedWhatsAppNumber || "+91 90808 45897"}). Click to view details.`
                : `WhatsApp Bot is Disconnected. Click to pair (${waStatus.formattedNumber || formattedWhatsAppNumber || "+91 90808 45897"}).`
            }
          >
            <div className="wa-header-icon-wrap">
              <FaWhatsapp />
            </div>
            <div className="wa-header-info">
              <span className="wa-header-label">
                WhatsApp ({waStatus.formattedNumber || formattedWhatsAppNumber || "+91 90808 45897"})
              </span>
              <span className={`wa-header-badge ${waStatus.connected ? "active" : "inactive"}`}>
                {waStatus.connected ? "🟢 Connected & Live" : "🔴 Link with Phone"}
              </span>
            </div>
          </div>

          {/* FEATURE VISIBILITY TOGGLE */}
          <div
            className="homam-status-toggle-card"
            onClick={handleToggleStatus}
            style={{ cursor: "pointer" }}
            title={isHomamActive ? "Click to Turn OFF and Hide Nakshatra Homam from Website" : "Click to Turn ON and Make Nakshatra Homam Live"}
          >
            <div className="toggle-info">
              <span className="toggle-label">Website Visibility</span>
              <span className={`status-pill ${isHomamActive ? "active" : "inactive"}`}>
                {isHomamActive ? "🟢 Active on Website" : "⚪ Hidden from Website"}
              </span>
            </div>
            <div className="toggle-switch" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                id="homam-visibility-checkbox"
                checked={Boolean(isHomamActive)}
                onChange={handleToggleStatus}
                disabled={toggleLoading}
              />
              <label htmlFor="homam-visibility-checkbox" className="toggle-slider"></label>
            </div>
          </div>
        </div>
      </div>

      {/* METRICS ROW */}
      <div className="homam-stats-row">
        <div className="homam-stat-card registrations">
          <div className="stat-icon-wrap">
            <FaCalendarCheck />
          </div>
          <div className="stat-text">
            <h3>{bookings.length}</h3>
            <p>Total Registrations</p>
          </div>
        </div>

        <div className="homam-stat-card participants">
          <div className="stat-icon-wrap">
            <FaUsers />
          </div>
          <div className="stat-text">
            <h3>{totalParticipants}</h3>
            <p>Total Devotees / Family</p>
          </div>
        </div>

        <div className="homam-stat-card today">
          <div className="stat-icon-wrap">
            <FaStar />
          </div>
          <div className="stat-text">
            <h3>{todayBookingsCount}</h3>
            <p>Registered Today</p>
          </div>
        </div>
      </div>

      {/* CONTROLS & DOWNLOAD BAR */}
      <div className="homam-controls-bar">
        <div className="homam-search-filter-group">
          {/* Search Box */}
          <div className="homam-search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by devotee name, mobile, city, or star..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Star Filter */}
          <select
            className="homam-filter-select"
            value={starFilter}
            onChange={(e) => setStarFilter(e.target.value)}
          >
            <option value="All">All 27 Nakshatras (அனைத்து நட்சத்திரங்கள்)</option>
            {MOCK_NAKSHATRAS.map((star) => (
              <option key={star.id} value={star.id}>
                Day {star.orderIndex}: {star.nameEn} ({star.nameTa}) • {star.assignedDate}
              </option>
            ))}
          </select>
        </div>

        {/* 1-CLICK DOWNLOAD EXCEL BUTTON */}
        <button
          className={`homam-download-btn ${downloadingExcel ? "downloading" : ""}`}
          onClick={handleDownloadExcel}
          disabled={downloadingExcel}
          title="Download complete registered people list as a styled Excel spreadsheet (.xlsx)"
        >
          <FaFileExcel className="homam-excel-icon" />
          <span>{downloadingExcel ? "Exporting Excel (.xlsx)..." : "Download Registered People List"}</span>
        </button>
      </div>

      {/* TABLE SECTION */}
      <div className="homam-table-card">
        {loading ? (
          <div className="homam-loading-state">
            <div className="homam-loading-spinner"></div>
            <h3>Fetching Homam Registrations...</h3>
            <p>Connecting to secure database</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="homam-empty-state">
            <div className="empty-icon-wrap">
              <FaStar />
            </div>
            <h3>No Registrations Found</h3>
            <p>
              {search || starFilter !== "All"
                ? "Try adjusting your search query or Nakshatra filter."
                : "No devotees have registered for Nakshatra Homam yet."}
            </p>
          </div>
        ) : (
          <div className="homam-table-responsive">
            <table className="homam-data-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Booking ID</th>
                  <th>Devotee Name</th>
                  <th>Contact Details</th>
                  <th>Honored Star (Day)</th>
                  <th>Seva Plan & Amount</th>
                  <th>UTR Number</th>
                  <th>Payment Status</th>
                  <th>Registered Time</th>
                  <th>Verification Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((b, index) => (
                  <tr key={b._id || index}>
                    <td>{index + 1}</td>

                    <td>
                      <span style={{ fontSize: "0.8rem", fontWeight: 800, color: "#0f2d5c", background: "#fef3c7", padding: "3px 8px", borderRadius: 6, display: "inline-block" }}>
                        {b.bookingId || `SGSMH${b._id?.slice(-5)}`}
                      </span>
                    </td>

                    <td>
                      <div className="devotee-cell">
                        <span className="devotee-name">{b.name}</span>
                        {b.gothram && (
                          <span className="devotee-city">Gothram: {b.gothram}</span>
                        )}
                        <span className="devotee-city">{b.city}</span>
                      </div>
                    </td>

                    <td>
                      <div className="contact-cell">
                        <span className="contact-phone">
                          <FaPhoneAlt style={{ fontSize: "0.7rem", marginRight: 4 }} />
                          {b.mobile}
                        </span>
                        {b.whatsappNumber && (
                          <span className="contact-wa">
                            <FaWhatsapp style={{ fontSize: "0.75rem", marginRight: 4 }} />
                            {b.whatsappNumber}
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      {(() => {
                        const matched = (b.nakshatraNameEn && MOCK_NAKSHATRAS.find(s => s.nameEn.toLowerCase() === b.nakshatraNameEn.trim().toLowerCase()))
                          || (b.nakshatraNameTa && MOCK_NAKSHATRAS.find(s => s.nameTa === b.nakshatraNameTa.trim()))
                          || MOCK_NAKSHATRAS.find(s => s.id === Number(b.nakshatraId))
                          || null;

                        const dayNum = matched ? matched.orderIndex : b.nakshatraId;
                        const starTitle = b.nakshatraNameEn
                          ? `${b.nakshatraNameEn} ${b.nakshatraNameTa ? `(${b.nakshatraNameTa})` : ''}`
                          : (matched ? `${matched.nameEn} (${matched.nameTa})` : `Star ${b.nakshatraId}`);
                        const dateDisplay = b.homamDate || (matched ? matched.assignedDate : b.preferredDay);

                        return (
                          <>
                            <div className="star-badge">
                              <FaStar style={{ fontSize: "0.7rem" }} />
                              <span>
                                Day {dayNum}: {starTitle}
                              </span>
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "#64748b", marginTop: 2 }}>
                              {dateDisplay}
                            </div>
                          </>
                        );
                      })()}
                    </td>

                    <td>
                      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f2d5c" }}>
                        {b.planName || "Individual Homam"}
                      </div>
                      <div style={{ fontSize: "0.85rem", fontWeight: 800, color: "#b8860b" }}>
                        ₹{((b.planAmount && b.planAmount > 1) ? b.planAmount : 1001).toLocaleString("en-IN")}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "#92400e" }}>
                        Allowed: {b.membersAllowed > 1 ? `Up to ${b.membersAllowed} Members` : "1 Devotee"}
                      </div>
                    </td>

                    <td>
                      {b.utrNumber ? (
                        <span style={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 700, color: "#0f2d5c", background: "#f1f5f9", padding: "3px 7px", borderRadius: 4, display: "inline-block" }}>
                          {b.utrNumber}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.75rem", color: "#0284c7", fontWeight: 600, background: "#e0f2fe", padding: "3px 8px", borderRadius: 12, display: "inline-block" }}>
                          Direct Seva Offering
                        </span>
                      )}
                    </td>

                    <td>
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: "0.75rem",
                        fontWeight: 800,
                        padding: "4px 9px",
                        borderRadius: 12,
                        background: b.paymentStatus === "Verified" ? "#dcfce7" : b.paymentStatus === "Rejected" ? "#fee2e2" : "#fef3c7",
                        color: b.paymentStatus === "Verified" ? "#15803d" : b.paymentStatus === "Rejected" ? "#b91c1c" : "#b45309",
                        border: `1px solid ${b.paymentStatus === "Verified" ? "#86efac" : b.paymentStatus === "Rejected" ? "#fca5a5" : "#fde68a"}`
                      }}>
                        {b.paymentStatus === "Verified" ? "✓ Verified" : b.paymentStatus === "Rejected" ? "✕ Rejected" : "● Pending"}
                      </span>
                    </td>

                    <td>
                      <div className="date-text">
                        <span>{formatDate(b.createdAt)}</span>
                        <span className="date-sub">{formatTime(b.createdAt)}</span>
                      </div>
                    </td>

                    <td>
                      <div className="action-buttons-cell">
                        {/* APPROVE BUTTON (TICK ICON WITH PASTEL GLASSMORPHISM) */}
                        {b.paymentStatus !== "Verified" && b.paymentStatus !== "Approved" && (
                          <button
                            type="button"
                            className="btn-action-approve"
                            onClick={() => handleVerifyPayment(b._id, "approve")}
                            title="Approve Payment (Verify)"
                            aria-label="Approve Payment"
                          >
                            <FaCheck />
                          </button>
                        )}

                        {/* REJECT BUTTON (WRONG ICON WITH PASTEL GLASSMORPHISM) */}
                        {b.paymentStatus !== "Rejected" && (
                          <button
                            type="button"
                            className="btn-action-reject"
                            onClick={() => handleVerifyPayment(b._id, "reject")}
                            title="Reject Payment"
                            aria-label="Reject Payment"
                          >
                            <FaTimes />
                          </button>
                        )}

                        {/* INSPECT DETAILS */}
                        <button
                          type="button"
                          className="btn-action-inspect"
                          onClick={() => setSelectedBooking(b)}
                          title="View Full Devotee Details"
                          aria-label="Inspect Devotee Details"
                        >
                          <FaEye />
                        </button>

                        {/* DELETE */}
                        <button
                          type="button"
                          className="btn-action-delete"
                          onClick={() => handleDelete(b._id)}
                          title="Delete Registration"
                          aria-label="Delete Registration"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DEVOTEE DETAILS MODAL */}
      {selectedBooking && (
        <div className="homam-modal-overlay">
          <div className="homam-modal-card">
            <div className="homam-modal-header">
              <h2>
                <FaStar style={{ color: "#b8860b" }} /> Devotee Booking Details
              </h2>
              <button
                className="homam-modal-close"
                onClick={() => setSelectedBooking(null)}
              >
                &times;
              </button>
            </div>

            <div className="homam-modal-body">
              <div className="homam-modal-field">
                <label>Booking ID</label>
                <p style={{ fontWeight: 800, color: "#0f2d5c" }}>
                  {selectedBooking.bookingId || `SGSMH${selectedBooking._id?.slice(-5)}`}
                </p>
              </div>

              <div className="homam-modal-field">
                <label>Devotee Name</label>
                <p>{selectedBooking.name}</p>
              </div>

              <div className="homam-modal-field">
                <label>City / Location</label>
                <p>{selectedBooking.city}</p>
              </div>

              <div className="homam-modal-field">
                <label>Mobile Number</label>
                <p>{selectedBooking.mobile}</p>
              </div>

              <div className="homam-modal-field">
                <label>WhatsApp Number</label>
                <p>{selectedBooking.whatsappNumber || selectedBooking.mobile}</p>
              </div>

              <div className="homam-modal-field">
                <label>Honored Nakshatra</label>
                <p>
                  Day {selectedBooking.nakshatraId}: {selectedBooking.nakshatraNameEn}{" "}
                  {selectedBooking.nakshatraNameTa ? `(${selectedBooking.nakshatraNameTa})` : ""}
                </p>
              </div>

              <div className="homam-modal-field">
                <label>Homam Date</label>
                <p>{selectedBooking.homamDate || selectedBooking.preferredDay}</p>
              </div>

              <div className="homam-modal-field">
                <label>Seva Plan</label>
                <p style={{ fontWeight: 700, color: "#0f2d5c" }}>
                  {selectedBooking.planName || "Individual Homam"} — ₹{((selectedBooking.planAmount && selectedBooking.planAmount > 1) ? selectedBooking.planAmount : 1001).toLocaleString("en-IN")}
                </p>
              </div>

              <div className="homam-modal-field">
                <label>Members Allowed</label>
                <p>{selectedBooking.membersAllowed > 1 ? `Up to ${selectedBooking.membersAllowed} Family Members` : "1 Devotee"}</p>
              </div>

              {/* Family Members for Homam */}
              {selectedBooking.familyMembers && selectedBooking.familyMembers.length > 0 && (
                <div className="homam-modal-field full-width" style={{ marginTop: 8 }}>
                  <label style={{ color: "#b8860b", fontWeight: 800 }}>
                    👨‍👩‍👧‍👦 Family Members for Sacred Homam ({selectedBooking.familyMembers.length})
                  </label>
                  <div style={{ background: "#FAF7F2", border: "1.5px solid #EADDC7", borderRadius: 12, padding: 12, marginTop: 6, overflowX: "auto" }}>
                    <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #EADDC7", textAlign: "left", color: "#64748b" }}>
                          <th style={{ padding: "6px 8px" }}>#</th>
                          <th style={{ padding: "6px 8px" }}>Member Name</th>
                          <th style={{ padding: "6px 8px" }}>Relationship</th>
                          <th style={{ padding: "6px 8px" }}>Birth Star</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBooking.familyMembers.map((m, idx) => (
                          <tr key={idx} style={{ borderBottom: "1px dashed #e2e8f0" }}>
                            <td style={{ padding: "6px 8px", fontWeight: 700, color: "#64748b" }}>{idx + 1}</td>
                            <td style={{ padding: "6px 8px", fontWeight: 700, color: "#0f2d5c" }}>{m.name || "Devotee"}</td>
                            <td style={{ padding: "6px 8px" }}>
                              <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 10, fontSize: "0.75rem", fontWeight: 700 }}>
                                {m.relationship || (idx === 0 ? "Primary Devotee" : "Family Member")}
                              </span>
                            </td>
                            <td style={{ padding: "6px 8px", color: "#0f2d5c" }}>{m.nakshatra || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="homam-modal-field">
                <label>12-Digit UTR / Transaction ID</label>
                <p style={{ fontFamily: "monospace", fontWeight: 800, color: "#0f2d5c" }}>
                  {selectedBooking.utrNumber || "Direct Seva Offering (To be arranged with Devotee)"}
                </p>
              </div>

              <div className="homam-modal-field">
                <label>Payment Verification Status</label>
                <p style={{ fontWeight: 800, color: selectedBooking.paymentStatus === "Verified" ? "#16a34a" : selectedBooking.paymentStatus === "Rejected" ? "#dc2626" : "#d97706" }}>
                  {selectedBooking.paymentStatus || "Pending"}
                </p>
              </div>

              {selectedBooking.rejectionReason && (
                <div className="homam-modal-field full-width">
                  <label style={{ color: "#dc2626" }}>Rejection Reason</label>
                  <p style={{ color: "#dc2626" }}>{selectedBooking.rejectionReason}</p>
                </div>
              )}

              <div className="homam-modal-field">
                <label>Registered Date & Time</label>
                <p>
                  {formatDate(selectedBooking.createdAt)} at {formatTime(selectedBooking.createdAt)}
                </p>
              </div>

              {selectedBooking.message && (
                <div className="homam-modal-field full-width">
                  <label>Special Prayers / Gothram / Notes</label>
                  <div className="prayer-box">{selectedBooking.message}</div>
                </div>
              )}
            </div>

            <div className="homam-modal-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {selectedBooking.paymentStatus !== "Verified" && (
                  <button
                    className="btn-modal-action approve"
                    onClick={() => handleVerifyPayment(selectedBooking._id, "approve")}
                    style={{ background: "#dcfce7", color: "#16a34a", border: "1px solid #86efac", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: "0.85rem" }}
                  >
                    ✓ Approve Payment
                  </button>
                )}
                {selectedBooking.paymentStatus !== "Rejected" && (
                  <button
                    className="btn-modal-action reject"
                    onClick={() => handleVerifyPayment(selectedBooking._id, "reject")}
                    style={{ background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: "0.85rem" }}
                  >
                    ✕ Reject Payment
                  </button>
                )}

                {/* 1-Click WhatsApp & Email Confirmation to Devotee with Family List */}
                {selectedBooking.paymentStatus === "Verified" && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSendWhatsAppNotification(selectedBooking)}
                      disabled={resendingWa}
                      style={{ background: "#10b981", color: "#ffffff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: resendingWa ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Send automated WhatsApp bot confirmation message to devotee"
                    >
                      <FaWhatsapp />
                      <span>{resendingWa ? "Sending WhatsApp..." : "Resend WhatsApp"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSendEmailNotification(selectedBooking)}
                      disabled={resendingEmail}
                      style={{ background: "#0284c7", color: "#ffffff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: resendingEmail ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Send official confirmation email to devotee"
                    >
                      <FaEnvelope />
                      <span>{resendingEmail ? "Sending Email..." : "Resend Email"}</span>
                    </button>
                    <a
                      href={`https://wa.me/91${(selectedBooking.whatsappNumber || selectedBooking.mobile).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(
                        `Om Sai Ram 🙏 Dear ${selectedBooking.name},\n\nYour 27 Nakshatra Maha Homam booking has been *VERIFIED & CONFIRMED* by Sri Gnana Sai Baba Mandir! ✨\n\n*Booking ID:* ${selectedBooking.bookingId || "SGSMH"}\n*Star:* Day ${selectedBooking.nakshatraId} • ${selectedBooking.nakshatraNameEn} (${selectedBooking.nakshatraNameTa || ""})\n*Homam Date:* ${selectedBooking.homamDate || selectedBooking.preferredDay}\n*Seva Plan:* ${selectedBooking.planName} (₹${((selectedBooking.planAmount && selectedBooking.planAmount > 1) ? selectedBooking.planAmount : 1001).toLocaleString("en-IN")})\n\n*Family Members for Sacred Homam:*\n${
                          selectedBooking.familyMembers && selectedBooking.familyMembers.length > 0
                            ? selectedBooking.familyMembers.map((m, idx) => `${idx + 1}. ${m.name} (${m.relationship}${m.nakshatra ? ` - ${m.nakshatra}` : ""})`).join("\n")
                            : `1. ${selectedBooking.name} (Primary Devotee)`
                        }\n\nMay Sri Gnana Sai Baba bless you and your family with peace, prosperity, and good health! 🙏\n- Sri Gnana Sai Baba Mandir Seva Trust`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ background: "#25D366", color: "#ffffff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: "0.85rem", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
                    >
                      <FaWhatsapp />
                      <span>Open WhatsApp Web</span>
                    </a>
                  </>
                )}

                {/* 1-Click WhatsApp & Email Rejection Notice to Devotee */}
                {selectedBooking.paymentStatus === "Rejected" && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSendWhatsAppNotification(selectedBooking)}
                      disabled={resendingWa}
                      style={{ background: "#ef4444", color: "#ffffff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: resendingWa ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Resend rejection notice via WhatsApp bot"
                    >
                      <FaWhatsapp />
                      <span>{resendingWa ? "Sending WhatsApp..." : "Resend Rejection WhatsApp"}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSendEmailNotification(selectedBooking)}
                      disabled={resendingEmail}
                      style={{ background: "#b91c1c", color: "#ffffff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: resendingEmail ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: 6 }}
                      title="Resend rejection notice via official Mandir email"
                    >
                      <FaEnvelope />
                      <span>{resendingEmail ? "Sending Email..." : "Resend Rejection Email"}</span>
                    </button>
                  </>
                )}
              </div>

              <button
                className="btn-modal-close"
                onClick={() => setSelectedBooking(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          🎉 DIVINE HOMAM APPROVAL & WHATSAPP CONFIRMATION MODAL
      ========================================================= */}
      {approvalModalData && (
        <div className="homam-approval-modal-overlay" onClick={() => setApprovalModalData(null)}>
          <div className="homam-approval-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="approval-modal-header-accent"></div>

            <button
              type="button"
              className="btn-modal-close-round"
              onClick={() => setApprovalModalData(null)}
              title="Close"
            >
              <FaTimes />
            </button>

            <div className="approval-hero-icon-container">
              <div className="approval-icon-pulse-ring ring-1"></div>
              <div className="approval-icon-pulse-ring ring-2"></div>
              <div className="approval-icon-circle">
                <FaCheck className="approval-check-svg" />
              </div>
            </div>

            <div className="approval-modal-text-group">
              <span className="approval-status-badge">
                <span className="dot-pulse"></span> Registration Confirmed &amp; Verified
              </span>
              <h2>Homam Registration Confirmed!</h2>
              <p className="approval-subtext">
                Devotee has been formally enrolled into the sacred 27 Nakshatra Maha Homam.
              </p>
            </div>

            {/* Automated WhatsApp Dispatch Card */}
            <div className="whatsapp-dispatch-card">
              <div className="wa-dispatch-top">
                <div className="wa-brand-label">
                  <span className="wa-icon-pill">
                    <FaWhatsapp />
                  </span>
                  <div>
                    <h4>WhatsApp Confirmation Dispatched</h4>
                    <p>Automated notification delivered via Mandir Bot</p>
                  </div>
                </div>
                <div className="wa-recipient-pill">
                  <FaPhoneAlt style={{ fontSize: "0.7rem", marginRight: 4 }} />
                  +91 {(approvalModalData.whatsappNumber || approvalModalData.mobile || "").replace(/\D/g, "").slice(-10)}
                </div>
              </div>

              <div className="wa-dispatch-body">
                <div className="wa-message-snippet">
                  <span className="wa-snippet-tag">MESSAGE DELIVERED TO DEVOTEE</span>
                  <p>
                    🌸 <strong>SRI GNANA SAI BABA MANDIR</strong> — Homam &amp; Seva Confirmed. Om Sai Ram, <strong>{approvalModalData.name}</strong> 🙏 Your registration for <strong>{approvalModalData.nakshatraNameEn || "Nakshatra"} Homam</strong> ({approvalModalData.homamDate || "Scheduled Day"}) has been verified with Mandir venue, Vedic timings &amp; dress code guidelines.
                  </p>
                </div>
              </div>
            </div>

            {/* Devotee Quick Summary Grid */}
            <div className="approval-devotee-grid">
              <div className="grid-pill">
                <span className="pill-lbl">Booking Token</span>
                <span className="pill-val code">{approvalModalData.bookingId}</span>
              </div>
              <div className="grid-pill">
                <span className="pill-lbl">Devotee Name</span>
                <span className="pill-val">{approvalModalData.name}</span>
              </div>
              <div className="grid-pill">
                <span className="pill-lbl">Honored Star</span>
                <span className="pill-val star">
                  <FaStar style={{ color: "#f59e0b", fontSize: "0.75rem", marginRight: 4 }} />
                  {approvalModalData.nakshatraNameEn} {approvalModalData.nakshatraNameTa ? `(${approvalModalData.nakshatraNameTa})` : ""}
                </span>
              </div>
              <div className="grid-pill">
                <span className="pill-lbl">Seva Offering</span>
                <span className="pill-val price">
                  ₹{((approvalModalData.planAmount && approvalModalData.planAmount > 1) ? approvalModalData.planAmount : 1001).toLocaleString("en-IN")} • {approvalModalData.planName || "Individual Homam"}
                </span>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="approval-modal-actions">
              <button
                type="button"
                className="btn-action-chat-wa"
                style={{ background: "#10b981", cursor: resendingWa ? "not-allowed" : "pointer" }}
                onClick={() => handleSendWhatsAppNotification(approvalModalData)}
                disabled={resendingWa}
                title="Resend automated WhatsApp bot confirmation message"
              >
                <FaWhatsapp style={{ fontSize: "1.1rem" }} />
                <span>{resendingWa ? "Sending..." : "Resend WhatsApp Message"}</span>
              </button>

              <button
                type="button"
                className="btn-action-chat-wa"
                style={{ background: "#0284c7", cursor: resendingEmail ? "not-allowed" : "pointer" }}
                onClick={() => handleSendEmailNotification(approvalModalData)}
                disabled={resendingEmail}
                title="Resend official confirmation email"
              >
                <FaEnvelope style={{ fontSize: "1.0rem" }} />
                <span>{resendingEmail ? "Sending..." : "Resend Email"}</span>
              </button>

              <a
                href={`https://wa.me/91${(approvalModalData.whatsappNumber || approvalModalData.mobile || "").replace(/\D/g, "").slice(-10)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-action-chat-wa"
                style={{ background: "#25D366" }}
              >
                <FaWhatsapp style={{ fontSize: "1.1rem" }} />
                <span>Open Chat</span>
              </a>

              <button
                type="button"
                className="btn-action-confirm-done"
                onClick={() => setApprovalModalData(null)}
              >
                Done &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          ❌ CUSTOM REJECTION MODAL (Replaces browser prompt)
      ========================================================= */}
      {rejectModalData && (
        <div className="homam-approval-modal-overlay" onClick={() => setRejectModalData(null)}>
          <div className="homam-approval-modal-card reject-theme" onClick={(e) => e.stopPropagation()}>
            <div className="approval-modal-header-accent"></div>

            <button
              type="button"
              className="btn-modal-close-round"
              onClick={() => setRejectModalData(null)}
              title="Cancel"
            >
              <FaTimes />
            </button>

            <div className="approval-hero-icon-container">
              <div className="approval-icon-circle reject">
                <FaTimes className="approval-check-svg" />
              </div>
            </div>

            <div className="approval-modal-text-group">
              <h2>Reject Registration Payment</h2>
              <p className="approval-subtext">
                Mark payment for <strong>{rejectModalData.booking?.name}</strong> ({rejectModalData.booking?.bookingId}) as rejected.
              </p>
            </div>

            <div className="reject-reason-input-wrap">
              <label htmlFor="rejectionReasonInput">Reason for Rejection (visible to devotee):</label>
              <textarea
                id="rejectionReasonInput"
                rows={3}
                placeholder="e.g., UPI payment reference / UTR not found on Mandir statement. Please re-submit payment details."
                value={rejectModalData.reason}
                onChange={(e) => setRejectModalData((prev) => ({ ...prev, reason: e.target.value }))}
              />
            </div>

            {/* Notification dispatch note */}
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", margin: "10px 0 16px 0", fontSize: "0.82rem", color: "#991b1b", display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
                <span>📬 Automated Notifications dispatched on Rejection:</span>
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: "0.8rem", color: "#7f1d1d" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <FaWhatsapp style={{ color: "#16a34a" }} /> WhatsApp Rejection Alert (+91 {(rejectModalData.booking?.whatsappNumber || rejectModalData.booking?.mobile || "").replace(/\D/g, "").slice(-10)})
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <FaEnvelope style={{ color: "#b91c1c" }} /> Official Rejection Email ({rejectModalData.booking?.email || "Linked Devotee Email"})
                </span>
              </div>
            </div>

            <div className="approval-modal-actions">
              <button
                type="button"
                className="btn-action-cancel"
                onClick={() => setRejectModalData(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-action-confirm-reject"
                onClick={() => handleVerifyPayment(rejectModalData.id, "reject", rejectModalData.reason || "Payment verification failed")}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          💬 CLEAN, MINIMALIST WHATSAPP POPUP MODAL
      ========================================================= */}
      {showWaPairModal && (
        <div className="wa-clean-overlay" onClick={() => setShowWaPairModal(false)}>
          <div className="wa-clean-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="wa-clean-header">
              <div className="wa-clean-header-title-group">
                <h3>WhatsApp Automation</h3>
                <p>Sri Gnana Sai Baba Mandir • Ambattur</p>
              </div>
              <button
                type="button"
                className="wa-clean-close-btn"
                onClick={() => setShowWaPairModal(false)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="wa-clean-body">
              {(waModalMode === "connected" || (waModalMode === "auto" && waStatus.connected)) ? (
                <>
                  {/* Clean Connected Account Card */}
                  <div className="wa-clean-account-row">
                    <div className="wa-clean-avatar">
                      <FaWhatsapp />
                    </div>
                    <div className="wa-clean-account-text">
                      <div className="wa-clean-phone">
                        {waStatus.formattedNumber || formattedWhatsAppNumber || "+91 90808 45897"}
                      </div>
                      <div className="wa-clean-sub">
                        {waStatus.accountName && waStatus.accountName !== "~"
                          ? waStatus.accountName
                          : "Sri Gnana Sai Baba Mandir"}
                      </div>
                    </div>
                    <span className="wa-clean-badge-connected">
                      <span className="wa-clean-dot" /> Connected
                    </span>
                  </div>

                  {/* Switch to Link New Phone Mode */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", padding: "10px 14px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div>
                      <span style={{ fontSize: "0.82rem", color: "#1e293b", fontWeight: 700, display: "block" }}>Link a Different WhatsApp Phone?</span>
                      <span style={{ fontSize: "0.74rem", color: "#64748b" }}>Pair a new mobile number or generate fresh code</span>
                    </div>
                    <button
                      type="button"
                      className="wa-clean-btn-action secondary"
                      onClick={() => {
                        setWaModalMode("pair");
                        setPairingPhoneInput("");
                        setTestMobile("");
                      }}
                      style={{ padding: "6px 14px", fontSize: "0.8rem", whiteSpace: "nowrap" }}
                    >
                      <FaSyncAlt /> Change / Pair Phone
                    </button>
                  </div>

                  {/* Config Box in Connected Mode */}
                  <div className="wa-clean-config-box">
                    <label htmlFor="connectedPhoneInput">Mandir WhatsApp Contact Number:</label>
                    <div className="wa-clean-input-group">
                      <span className="wa-clean-prefix">+91</span>
                      <input
                        id="connectedPhoneInput"
                        type="tel"
                        value={pairingPhoneInput}
                        onChange={(e) => handlePhoneInputChange(e.target.value)}
                        placeholder={waStatus.connectedNumber?.slice(-10) || "e.g. 9080845897"}
                      />
                      {pairingPhoneInput && (
                        <button
                          type="button"
                          className="wa-clean-btn-clear"
                          onClick={handleClearPhoneInput}
                          title="Clear number to enter a new one"
                        >
                          <FaTimes />
                        </button>
                      )}
                      <button
                        type="button"
                        className="wa-clean-btn-save-num"
                        onClick={() => handleSaveWhatsAppNumberOnly(pairingPhoneInput)}
                        disabled={savingNumberOnly || !pairingPhoneInput || pairingPhoneInput.length < 10}
                        title="Save number across website and integrations"
                      >
                        <FaSave className={savingNumberOnly ? "fa-spin" : ""} />
                        <span>{savingNumberOnly ? "Saving..." : "Save"}</span>
                      </button>
                    </div>
                    <p className="wa-clean-hint">
                      This number is displayed on the website floating button, subnavbar, registration forms, and emails.
                    </p>
                  </div>

                  {/* Clean Single Test Input */}
                  <div className="wa-clean-test-box">
                    <label htmlFor="cleanTestInput">
                      Send test message (to +91 {testMobile || pairingPhoneInput}):
                    </label>
                    <div className="wa-clean-input-group">
                      <span className="wa-clean-prefix">+91</span>
                      <input
                        id="cleanTestInput"
                        type="tel"
                        value={testMobile}
                        onChange={(e) => setTestMobile(e.target.value.replace(/\D/g, "").slice(-10))}
                        placeholder={pairingPhoneInput || "Enter 10-digit number"}
                      />
                      {testMobile && (
                        <button
                          type="button"
                          className="wa-clean-btn-clear"
                          onClick={() => setTestMobile("")}
                          title="Clear test number"
                        >
                          <FaTimes />
                        </button>
                      )}
                      <button
                        type="button"
                        className="wa-clean-btn-send"
                        onClick={handleSendTestMessage}
                        disabled={sendingTestWa || !testMobile || testMobile.length < 10}
                      >
                        <FaPaperPlane className={sendingTestWa ? "fa-spin" : ""} />
                        <span>{sendingTestWa ? "Sending..." : "Send"}</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Pair Mode Top Navigation Bar */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#111827" }}>Enter Phone Number to Link:</span>
                    {waStatus.connected && (
                      <button
                        type="button"
                        onClick={() => setWaModalMode("connected")}
                        style={{ background: "transparent", border: "none", color: "#2563eb", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
                      >
                        ← Back to Connected Device
                      </button>
                    )}
                  </div>

                  {/* Dynamic WhatsApp Phone Input */}
                  <div className="wa-clean-config-box">
                    <div className="wa-clean-input-group">
                      <span className="wa-clean-prefix">+91</span>
                      <input
                        id="pairingPhoneInput"
                        type="tel"
                        value={pairingPhoneInput}
                        onChange={(e) => handlePhoneInputChange(e.target.value)}
                        placeholder="Enter 10-digit mobile number"
                        autoFocus
                      />
                      {pairingPhoneInput && (
                        <button
                          type="button"
                          className="wa-clean-btn-clear"
                          onClick={handleClearPhoneInput}
                          title="Clear number to enter a new one"
                        >
                          <FaTimes />
                        </button>
                      )}
                      <button
                        type="button"
                        className="wa-clean-btn-save-num"
                        onClick={() => handleSaveWhatsAppNumberOnly(pairingPhoneInput)}
                        disabled={savingNumberOnly || !pairingPhoneInput || pairingPhoneInput.length < 10}
                        title="Save number across website without pairing right now"
                      >
                        <FaSave className={savingNumberOnly ? "fa-spin" : ""} />
                        <span>{savingNumberOnly ? "Saving..." : "Save"}</span>
                      </button>
                    </div>
                    <p className="wa-clean-hint">
                      Enter any mobile number. Click <strong>Generate code</strong> to link it via WhatsApp.
                    </p>
                  </div>

                  {/* Pairing Card */}
                  <div className="wa-clean-pair-box">
                    <span className="wa-clean-pair-title">
                      Pairing Code generated for (+91 {codeGeneratedForNumber || pairingPhoneInput || "..."}):
                    </span>
                    <div className="wa-clean-code-display">
                      {requestingPairCode ? (
                        <span className="wa-clean-loading-txt"><FaSyncAlt className="fa-spin" /> Generating code...</span>
                      ) : (
                        waStatus.pairingCode || "------"
                      )}
                    </div>
                    <div className="wa-clean-pair-btn-row">
                      <button
                        type="button"
                        className="wa-clean-btn-action"
                        onClick={() => {
                          if (!waStatus.pairingCode) {
                            handleRequestPairingCode(pairingPhoneInput);
                            return;
                          }
                          navigator.clipboard.writeText(waStatus.pairingCode);
                          setCodeCopied(true);
                          showToast("📋 Code copied!", "success");
                          setTimeout(() => setCodeCopied(false), 2000);
                        }}
                        disabled={!waStatus.pairingCode}
                      >
                        <FaCopy /> {codeCopied ? "Copied" : "Copy code"}
                      </button>
                      <button
                        type="button"
                        className="wa-clean-btn-action secondary"
                        onClick={() => handleRequestPairingCode(pairingPhoneInput)}
                        disabled={requestingPairCode || !pairingPhoneInput || pairingPhoneInput.length < 10}
                      >
                        <FaSyncAlt className={requestingPairCode ? "fa-spin" : ""} /> {waStatus.pairingCode ? "New code" : "Generate code"}
                      </button>
                    </div>
                  </div>

                  {/* Clean Simple Instructions */}
                  <div className="wa-clean-instructions">
                    <ol>
                      <li>Open WhatsApp on phone <strong>(+91 {codeGeneratedForNumber || pairingPhoneInput || "your phone"})</strong></li>
                      <li>Go to <strong>Settings &gt; Linked Devices &gt; Link a Device</strong></li>
                      <li>Select <strong>Link with phone number instead</strong> &amp; enter the code above</li>
                    </ol>
                  </div>
                </>
              )}
            </div>

            {/* Clean Footer */}
            <div className="wa-clean-footer">
              {waStatus.connected ? (
                <button
                  type="button"
                  className="wa-clean-btn-unlink"
                  onClick={handleDisconnectWhatsApp}
                >
                  Disconnect
                </button>
              ) : (
                <span className="wa-clean-footer-hint">Auto-connects once code is entered</span>
              )}
              <button
                type="button"
                className="wa-clean-btn-close"
                onClick={() => setShowWaPairModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          🍞 IN-APP FLOATING TOAST NOTIFICATION
      ========================================================= */}
      {toast && (
        <div className={`homam-admin-toast ${toast.type || "info"}`}>
          <div className="toast-icon">
            {toast.type === "success" && <FaCheck />}
            {toast.type === "error" && <FaTimes />}
            {toast.type === "info" && <FaStar />}
          </div>
          <div className="toast-content">
            <span>{toast.message}</span>
          </div>
          <button type="button" className="toast-close" onClick={() => setToast(null)}>
            <FaTimes />
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageHomamBookings;
